import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Drawer, Form, Input, Select, Spin } from "antd"
import { Button, CheckboxInput } from "components"
import { IFilterPolicyData, IPerson } from "../../interface/policies"
import {
  GroupData,
  PaybandDataEntity,
  PaygradeDataEntity,
} from "pages/dashboard/documents/reports/component/EmployeeCategoryFilter"
import { useState } from "react"
import { useAppNotificationStore } from "stores"
import { adminPeopleService } from "services/adminPeopleService"
import { onQueryError } from "utils/utils"
import { useGet } from "utils/useFetch"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { CustomSelect } from "components/new-stuff/form"

interface IPayload {
  paybandIds?: string[]
  paygradeIds?: string[]
  customGroupIds?: string[]
}
interface props {
  open: boolean
  setOpenFilter: (open: boolean) => void
  handleApplyFilter: (data: any) => void
}

const FilterPolicy = ({ open, setOpenFilter, handleApplyFilter }: props) => {
  const { toast } = useAppNotificationStore()
  const [form] = Form.useForm()
  const [active, setActive] = useState<number | null>(null)
  const [preview, setPreview] = useState(false)
  const [selectedPaygrade, setSelectedPaygrade] = useState<string[]>([])
  const [selectedPayband, setSelectedPayband] = useState<string[]>([])
  const [selectedCustomGroup, setSelectedCustomGroup] = useState<string[]>([])
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([])
  const [employees, setEmployees] = useState<IPerson[]>([])
  const [allEmployees, setAllEmployees] = useState<IPerson[]>([])
  const { isLoading: isPaygradeLoading, data: paygrades } = useGet<
    IBaseRes<PaygradeDataEntity[]>
  >({
    url: "/admin/company/selectpaygrades?payTypeId=1",
    instanceKey: "newArchAxiosInstance",
    onSuccess() {},
  })
  const { isLoading: isPaybandLoading, data: paybands } = useGet<
    IBaseRes<PaybandDataEntity[]>
  >({
    url: "/admin/company/selectpaybands?payTypeId=1",
    instanceKey: "newArchAxiosInstance",
    onSuccess() {},
  })
  // New-arch policy groups are paginated (default page size 10); fetch a
  // large page so the filter lists every group, as the legacy call did.
  const { isLoading: isGroupsLoading, data: groups } = useQuery<
    IBaseRes<IPaginatedPayload<GroupData>>
  >({
    queryKey: [COMPANY_ENDPOINTS.SELECT_POLICY_GROUPS, 1000],
    queryFn: () =>
      companyService.selectPolicyGroups({ pageNumber: 1, pageSize: 1000 }),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const { mutate: filterEmployeeType, isLoading } = useMutation({
    // The legacy single-call filter has no new-arch equivalent, so compose it
    // from the per-id selects. Policy-group members come back without a
    // person_key, which CreatePolicy needs, so recover it from the employee
    // selection list.
    mutationFn: async (data: IPayload): Promise<IFilterPolicyData> => {
      const paybandIds = data.paybandIds ?? []
      const paygradeIds = data.paygradeIds ?? []
      const customGroupIds = data.customGroupIds ?? []

      const [paybandResults, paygradeResults, groupResults, peopleRes] =
        await Promise.all([
          Promise.all(
            paybandIds.map((id) => adminPeopleService.selectByPayband(id))
          ),
          Promise.all(
            paygradeIds.map((id) => adminPeopleService.selectByPaygrade(id))
          ),
          Promise.all(
            customGroupIds.map((id) => companyService.viewPolicyGroup(id))
          ),
          customGroupIds.length
            ? adminPeopleService.getForSelectionSimple({ active: true })
            : Promise.resolve(undefined),
        ])

      const personKeyById = new Map<number, string>(
        (peopleRes?.data?.docs ?? []).map((p: any) => [
          Number(p.person_id),
          String(p.person_key),
        ])
      )

      const toPerson = (p: any): IPerson => ({
        person_id: Number(p.person_id),
        person_key:
          p.person_key ?? personKeyById.get(Number(p.person_id)) ?? "",
        first_name: p.first_name ?? "",
        middle_name: p.middle_name ?? null,
        last_name: p.last_name ?? "",
        full_name: p.full_name ?? `${p.first_name ?? ""} ${p.last_name ?? ""}`,
      })

      return {
        people_in_payband: paybandResults
          .flatMap((res) => res?.data?.docs ?? [])
          .map(toPerson),
        people_in_paygrades: paygradeResults
          .flatMap((res) => res?.data?.docs ?? [])
          .map(toPerson),
        people_in_custom_groups: groupResults
          .flatMap((res) => res?.data?.members ?? [])
          .map(toPerson)
          .filter((p) => !!p.person_key),
      }
    },
    onSuccess(data: IFilterPolicyData) {
      setPreview(true)

      const employees = [
        ...(data?.people_in_custom_groups ?? []),
        ...(data?.people_in_payband ?? []),
        ...(data?.people_in_paygrades ?? []),
      ]

      const filteredEmployees = Array.from(
        new Map(employees.map((p) => [p.person_key, p])).values()
      )

      setEmployees(filteredEmployees || [])
      setAllEmployees(filteredEmployees || [])
      setSelectedEmployees(filteredEmployees.map((emp) => emp.person_key))
    },
    onError: onQueryError,
  })
  const data = [
    {
      title: "Paygrade",
      name: "paygrade",
      loading: isPaygradeLoading,
      data: paygrades?.data?.map((item) => ({
        label: item.description,
        value: item.paygrade_id,
      })),
      onChange: (value: string[]) => setSelectedPaygrade(value),
      count: selectedPaygrade.length,
    },
    {
      title: "Payband",
      name: "payband",
      loading: isPaybandLoading,
      data: paybands?.data?.map((item) => ({
        label: item.description,
        value: item.payband_id,
      })),
      onChange: (value: string[]) => setSelectedPayband(value),
      count: selectedPayband.length,
    },
    {
      title: "Custom Group",
      name: "custom group",
      loading: isGroupsLoading,
      data: groups?.data?.docs?.map((item) => ({
        label: item.description,
        value: item.policy_group_id,
      })),
      onChange: (value: string[]) => setSelectedCustomGroup(value),
      count: selectedCustomGroup.length,
    },
  ]
  const handleClick = () => {
    filterEmployeeType({
      paygradeIds: selectedPaygrade,
      paybandIds: selectedPayband,
      customGroupIds: selectedCustomGroup,
    })
  }
  const handleToggleEmployee = (id: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((empId) => empId !== id) : [...prev, id]
    )
  }

  return (
    <Drawer
      width={675}
      title={
        <span className=" text-padeBlack font-circular text-xl">
          Filter Options{" "}
        </span>
      }
      open={open}
      onClose={() => (preview ? setPreview(false) : setOpenFilter(false))}
      className="bg-white relative"
    >
      {!preview ? (
        <div>
          <div className="flex justify-between items-center w-full mb-5">
            <p className="text-sm text-padeBlack">
              Select multiple options to refine your search
            </p>
            <button
              className=" border border-neutral40 px-2 py-1 rounded-md text-sm text-padeBlack font-circular font-medium text-center"
              onClick={() => {
                setSelectedPaygrade([])
                setSelectedPayband([])
                setSelectedCustomGroup([])
                form.resetFields()
                setActive(null)
              }}
            >
              Clear All
            </button>
          </div>
          <div className="overflow-y-auto   pb-16 font-circular text-padeBlack w-full flex flex-col gap-3 ">
            {data?.map((item, index) => (
              <div className="border-2 border-[#C2C7D066] rounded-5px p-5 w-full  mb-3">
                <div
                  className=" flex justify-between items-center w-full  cursor-pointer"
                  onClick={() =>
                    index === active ? setActive(null) : setActive(index)
                  }
                >
                  <div className="flex items-center gap-3 ">
                    <h6 className="text-padeBlack font-bold text-base">
                      {item?.title}
                    </h6>
                    {item?.loading && (
                      <div>
                        <Spin spinning />
                      </div>
                    )}
                    {item?.count > 0 && (
                      <p className="w-[23px] h-[19px] border border-neutral40 bg-primary500 text-white text-xs rounded-[10px] flex items-center justify-center">
                        {item?.count}
                      </p>
                    )}
                  </div>
                  <button>
                    {index === active ? (
                      <ChevronUpIcon className="h-4 w-4 text-padeBlack" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-padeBlack" />
                    )}
                  </button>
                </div>
                {index === active && (
                  <Form
                    layout="vertical"
                    form={form}
                    // onFinish={onFinish}
                  >
                    <div className="flex flex-col gap-4 mt-[26px] ">
                      <CustomSelect
                        label={
                          <span className="font-circular text-padeBlack text-sm ">
                            Select employee's {item?.name}
                          </span>
                        }
                        name={item?.name}
                        mode="multiple"
                        // name={item?.title}
                        options={item?.data || []}
                        placeholder="Please Select"
                        isLoading={item?.loading}
                        onChange={item?.onChange}
                      />
                    </div>
                  </Form>
                )}
              </div>
            ))}
          </div>
          <div className="absolute bottom-0 flex justify-end left-0 right-0 h-[64px]  py-3 px-4 lg:px-6 bg-white  gap-4">
            <Button
              color="secondary"
              onClick={() => setOpenFilter(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={
                selectedPaygrade.length === 0 &&
                selectedPayband.length === 0 &&
                selectedCustomGroup.length === 0
              }
              onClick={
                selectedPaygrade.length === 0 &&
                selectedPayband.length === 0 &&
                selectedCustomGroup.length === 0
                  ? () => {}
                  : () => handleClick()
              }
              submitting={isLoading}
            >
              Apply Filter{" "}
            </Button>
          </div>
        </div>
      ) : allEmployees?.length > 0 ? (
        <div className="">
          <div className="w-full ">
            <Input
              className="w-full"
              placeholder="Search Employee"
              onChange={(e) => {
                const value = e.target.value.toLowerCase()
                const filtered = allEmployees.filter((emp) =>
                  emp.full_name.toLowerCase().includes(value)
                )
                setEmployees(filtered)
              }}
            />
          </div>

          <div className="mt-[33px] flex flex-col gap-3">
            {employees?.map((emp) => (
              <div
                key={emp.person_id}
                className="flex items-center gap-3"
              >
                <CheckboxInput
                  checked={selectedEmployees.includes(emp.person_key)}
                  onChange={() => handleToggleEmployee(emp.person_key)}
                />
                <p className="text-padeBlack">{emp?.full_name}</p>
              </div>
            ))}
          </div>
          <div className="absolute bottom-0 flex justify-end left-0 right-0 h-[64px]  py-3 px-4 lg:px-6 bg-white  gap-4">
            <Button
              color="secondary"
              onClick={() => setPreview(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={selectedEmployees?.length === 0}
              color="primary"
              onClick={
                selectedEmployees?.length === 0
                  ? () => {}
                  : () => handleApplyFilter(selectedEmployees)
              }
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="flex items-center justify-center  mt-16">
            No employees found with the selected criteria.
          </p>
        </div>
      )}
    </Drawer>
  )
}

export default FilterPolicy
